import type { RunnerCapabilityCoverage } from '@api/localRunners';
import type { AssigneeType } from '@api/processes';
import type { HintTone } from './CapabilityHint.styled';

export interface CoverageHint {
  tone: HintTone;
  text: string;
}

/**
 * Build the non-blocking capability hint for a LOCAL Task node's assignee
 * set. Returns null when there's nothing meaningful to say (no coverage
 * yet, agent needs no app, or no assignee chosen) so the caller renders
 * nothing. Capability is dynamic — this is a best-effort hint from the
 * last-seen runner profiles, never a hard gate.
 */
export function resolveCoverageHint(
  coverage: RunnerCapabilityCoverage | undefined,
  assigneeType: AssigneeType,
  agentLabel: string,
): CoverageHint | null {
  if (!coverage || coverage.requiredApps.length === 0) return null;
  if (coverage.totalAssignees === 0) return null;

  const noun = assigneeType === 'GROUP' ? 'members' : 'assignees';
  const app = agentLabel || coverage.requiredApps.join(', ');

  if (coverage.coveredCount >= coverage.totalAssignees) {
    return { tone: 'ok', text: `${app}-capable runner known for every assignee` };
  }
  if (coverage.coveredCount === 0) {
    return {
      tone: 'warn',
      text: `No ${assigneeType === 'GROUP' ? 'group member' : 'assignee'} has a known ${app}-capable runner`,
    };
  }
  return {
    tone: 'warn',
    text: `${coverage.coveredCount} of ${coverage.totalAssignees} ${noun} have a known ${app}-capable runner`,
  };
}
